import { useCallback, useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { storageFactory } from '../lib/storage-factory';

type StorageState<T> = [T, (newValue: T) => void];

export const safeJSONParse = <T>(value: string | null): T | null => {
  try {
    return JSON.parse(value ?? '') as T;
  } catch {
    // Don't need to do anything here, if the value fails to parse we return a null
    return null;
  }
};

export const useStorage = <T>(
  storageType: 'sessionStorage' | 'localStorage',
  key: string,
  defaultValue?: T,
): StorageState<T> => {
  const storage = storageFactory(storageType);
  const [value, setValue] = useState<T>(storage.get<T>(key, defaultValue));

  const updateValue = useCallback(
    (newValue: T) => {
      storage.set<T>(key, newValue);
      setValue(newValue);
    },
    [storage, key],
  );

  return [value, updateValue];
};

export const useLocalStorageJSONValue = <T>(
  key: string,
): [T | null, Dispatch<SetStateAction<T | null>>] => {
  const [value, setValue] = useState(safeJSONParse<T>(localStorage.getItem(key)));

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

export const useSessionStorage = <T>(key: string, defaultValue?: T): StorageState<T> =>
  useStorage<T>('sessionStorage', key, defaultValue);

export const useLocalStorage = <T>(key: string, defaultValue?: T): StorageState<T> =>
  useStorage<T>('localStorage', key, defaultValue);

type Key = string | number | symbol;
type StoreValue<K extends Key, T> = Partial<Record<K, T>> | null;

// Use this when you want to store multiple values to a single storage key e.g. when storing settings keyed to a site ID
export function useSiteLocalStorageJSONValue<K extends string | number | symbol, T>(
  key: string,
  subKey: K | undefined,
): [T | undefined, (value: T | undefined) => void, () => void] | undefined {
  const [value, setValue] = useState<T | undefined>(undefined);

  useEffect(() => {
    if (subKey === undefined) {
      setValue(undefined);
      return;
    }

    const storeValue = safeJSONParse<StoreValue<K, T>>(localStorage.getItem(key));

    if (storeValue === null) {
      setValue(undefined);
      return;
    }
    const newValue = storeValue[subKey];
    setValue(newValue);
  }, [key, subKey]);

  const setSubValue = useCallback(
    (newValue: T | undefined) => {
      if (subKey === undefined) throw new Error('Cannot set value when subkey is undefined');

      const storeValue: StoreValue<K, T> = {
        ...safeJSONParse<StoreValue<K, T>>(localStorage.getItem(key)),
        [subKey]: newValue,
      };

      localStorage.setItem(key, JSON.stringify(storeValue));
      setValue(newValue);
    },
    [key, subKey],
  );

  const clearSubValue = useCallback(() => {
    if (subKey === undefined) throw new Error('Cannot clear value when subkey is undefined');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [subKey]: _, ...otherKeys } = safeJSONParse<StoreValue<K, T>>(
      localStorage.getItem(key),
    );

    localStorage.setItem(key, JSON.stringify(otherKeys));
    setValue(undefined);
  }, [key, subKey]);

  if (subKey !== undefined) return [value, setSubValue, clearSubValue];

  return undefined;
}
